import {
    LayoutDashboard,
    MapPin,
    Users,
    Package,
    ShoppingCart,
    Calendar,
    Boxes,
    UserCircle,
    Settings as SettingsIcon,
    CreditCard,
    Bell,
    History,
    Truck,
    Wallet,
    BarChart3,
    LifeBuoy,
    type LucideIcon,
} from "lucide-react";
import type { PlanFeature } from "@lib/entitlements";

export interface NavItem {
    /** Route, also the first path segment used for the page title. */
    path: string;
    /** Key under `nav.*` in the translation files. */
    navKey: string;
    icon: LucideIcon;
    /** Shown with a lock (and explained) when the current plan does not include it. */
    feature?: PlanFeature;
}

export interface NavGroup {
    /** Key under `nav.groups.*`; omitted for the un-headed first block. */
    groupKey?: string;
    items: NavItem[];
}

export const MAIN_NAV: NavGroup[] = [
    { items: [{ path: "/", navKey: "dashboard", icon: LayoutDashboard }] },
    {
        groupKey: "masterData",
        items: [
            { path: "/areas", navKey: "areas", icon: MapPin },
            { path: "/riders", navKey: "riders", icon: Users },
            { path: "/products", navKey: "products", icon: Package },
            { path: "/stocks", navKey: "stock", icon: Boxes, feature: "stockManagement" },
        ],
    },
    {
        groupKey: "customers",
        items: [
            { path: "/customers", navKey: "customers", icon: ShoppingCart },
            { path: "/payments", navKey: "payments", icon: Wallet },
        ],
    },
    {
        groupKey: "operations",
        items: [
            { path: "/shifts", navKey: "shifts", icon: Calendar },
            { path: "/deliveries", navKey: "deliveries", icon: Truck },
            { path: "/movements", navKey: "movements", icon: History },
            { path: "/reports", navKey: "reports", icon: BarChart3 },
        ],
    },
];

export const ACCOUNT_NAV: NavItem[] = [
    { path: "/notifications", navKey: "notifications", icon: Bell },
    { path: "/support", navKey: "support", icon: LifeBuoy },
    { path: "/subscription", navKey: "subscription", icon: CreditCard },
    { path: "/profile", navKey: "profile", icon: UserCircle },
    { path: "/settings", navKey: "settings", icon: SettingsIcon },
];

/** Screens that still work while the account is locked to billing (see `billingOnly.ts`). */
export const BILLING_ONLY_NAV_PATHS = ["/subscription", "/support", "/profile"];

const ALL_ITEMS: NavItem[] = [...MAIN_NAV.flatMap((g) => g.items), ...ACCOUNT_NAV];

/** The `nav.*` key that names the page at this path, or null when there isn't one. */
export function navKeyForPath(pathname: string): string | null {
    const first = pathname.split("/").filter(Boolean)[0];
    if (!first) return "dashboard";
    if (first === "billing") return "subscription";
    return ALL_ITEMS.find((i) => i.path === `/${first}`)?.navKey ?? null;
}
